import { useState, useEffect } from "react";
import { X, Send } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "@/hooks/useTranslation";

export type InquiryType = "Demo Request" | "General Inquiry" | "Pilot Program";

interface Vida24ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
  inquiryType: InquiryType;
}

const RECIPIENT = "VidavoEU@vidavo.eu";

const Vida24ContactModal = ({ isOpen, onClose, inquiryType }: Vida24ContactModalProps) => {
  const { t } = useTranslation();
  const tr = t.vida24HomeCare.contactModal;
  const typeKey: "demo" | "general" | "pilot" =
    inquiryType === "Demo Request" ? "demo" : inquiryType === "Pilot Program" ? "pilot" : "general";
  const displayTitle = tr.titles[typeKey];
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    organization: "",
    jobTitle: "",
    country: "",
    email: "",
    phone: "",
    message: "",
  });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = `[${displayTitle}] ${form.firstName} ${form.lastName} – ${form.organization}`;

    const divider = "━".repeat(44);
    const renderRows = (rows: { label: string; value: string }[]) => {
      const filtered = rows.filter((r) => r.value && r.value.trim().length > 0);
      if (filtered.length === 0) return "";
      const pad = Math.max(...filtered.map((r) => r.label.length)) + 2;
      return filtered
        .map((r) => `  • ${(r.label + ":").padEnd(pad, " ")} ${r.value.trim()}`)
        .join("\n");
    };

    const sections: string[] = [];

    const contact = renderRows([
      { label: "Name", value: `${form.firstName} ${form.lastName}`.trim() },
      { label: "Email", value: form.email },
      { label: "Phone", value: form.phone },
    ]);
    if (contact) sections.push(`👤 CONTACT\n${contact}`);

    const org = renderRows([
      { label: "Company", value: form.organization },
      { label: "Job title", value: form.jobTitle },
      { label: "Country", value: form.country },
    ]);
    if (org) sections.push(`🏢 ORGANIZATION\n${org}`);

    if (form.message && form.message.trim().length > 0) {
      sections.push(`💬 MESSAGE\n  ${form.message.trim().replace(/\n/g, "\n  ")}`);
    }

    const body =
      `${divider}\n` +
      `  VIDA24 — ${inquiryType.toUpperCase()}\n` +
      `${divider}\n\n` +
      sections.join("\n\n") +
      `\n\n${divider}\n` +
      `Sent from vidavo.eu — Vida24 contact form`;

    const mailto = `mailto:${RECIPIENT}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        >
          <motion.div
            className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onClose}
              aria-label={tr.close}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-neutral-100 transition-colors z-10"
            >
              <X className="w-5 h-5 text-neutral-600" />
            </button>

            <div className="p-6 sm:p-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-primary mb-2">
                {displayTitle}
              </h2>
              <p className="text-neutral-600 mb-6">
                {tr.subtitle}
              </p>

              <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-neutral-700 mb-1">{tr.labels.firstName} *</label>
                  <input id="firstName" name="firstName" required value={form.firstName} onChange={handleChange}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/40" />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-neutral-700 mb-1">{tr.labels.lastName} *</label>
                  <input id="lastName" name="lastName" required value={form.lastName} onChange={handleChange}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/40" />
                </div>
                <div>
                  <label htmlFor="organization" className="block text-sm font-medium text-neutral-700 mb-1">{tr.labels.organization} *</label>
                  <input id="organization" name="organization" required value={form.organization} onChange={handleChange}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/40" />
                </div>
                <div>
                  <label htmlFor="jobTitle" className="block text-sm font-medium text-neutral-700 mb-1">{tr.labels.jobTitle}</label>
                  <input id="jobTitle" name="jobTitle" value={form.jobTitle} onChange={handleChange}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/40" />
                </div>
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-neutral-700 mb-1">{tr.labels.country}</label>
                  <input id="country" name="country" value={form.country} onChange={handleChange}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/40" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-neutral-700 mb-1">{tr.labels.email} *</label>
                  <input id="email" name="email" type="email" required value={form.email} onChange={handleChange}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/40" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="phone" className="block text-sm font-medium text-neutral-700 mb-1">{tr.labels.phone}</label>
                  <input id="phone" name="phone" type="tel" value={form.phone} onChange={handleChange}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/40" />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="message" className="block text-sm font-medium text-neutral-700 mb-1">{tr.labels.message}</label>
                  <textarea id="message" name="message" rows={4} value={form.message} onChange={handleChange}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/40" />
                </div>

                <input type="hidden" name="inquiryType" value={inquiryType} />

                <div className="sm:col-span-2 flex justify-end pt-2">
                  <button type="submit" className="button-primary inline-flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    {tr.submit}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Vida24ContactModal;